import {
  Button,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Delete from "@material-ui/icons/Delete";
import Warning from "@material-ui/icons/Warning";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SerialisedSubAssemblySearchAutocomplete from "../../Components/SerialisedSubAssemblySearchAutocomplete";

const CLOSED_SHIPMENT_STATUSES = ["delivered", "cancelled"];

const SERIALISED_SUB_ASSEMBLY_STOCK_ITEM_SKUS = ["CTN_EC_CHASSIS"];

export const isSerialisedSubAssemblyStockItem = (item) =>
  SERIALISED_SUB_ASSEMBLY_STOCK_ITEM_SKUS.includes(
    (item?.sku || "").toString().trim().toUpperCase(),
  );

export const getStockItemId = (item) =>
  item?.StockItemId || item?.StockItem?.id || item?.id;

export const getSubAssemblyStockItemId = (subAssembly) =>
  subAssembly?.StockItemId ||
  subAssembly?.ManufacturingEntry?.StockItemId ||
  subAssembly?.ManufacturingEntry?.StockItem?.id;

export const getSerialisedSubAssemblyAllocationsForStockItem = (
  shipmentData,
  stockItem,
) => {
  const stockItemId = getStockItemId(stockItem);
  if (!stockItemId) {
    return [];
  }
  return (shipmentData?.SerialisedSubAssemblies || []).filter(
    (subAssembly) =>
      getSubAssemblyStockItemId(subAssembly)?.toString() ===
      stockItemId.toString(),
  );
};

const getSubAssemblyMeta = (subAssembly) => {
  return subAssembly?.color;
};

export default function SerialisedSubAssemblyAllocation({
  stockItem,
  shipmentData,
  setShipmentData,
  editable,
}) {
  const { t } = useTranslation();
  const [addingSerial, setAddingSerial] = useState();
  const allocations = shipmentData?.SerialisedSubAssemblies || [];
  const isClosedShipment = CLOSED_SHIPMENT_STATUSES.includes(
    shipmentData?.status,
  );

  if (
    !shipmentData ||
    !stockItem ||
    shipmentData.shipmentDirection !== "between" ||
    !isSerialisedSubAssemblyStockItem(stockItem)
  ) {
    return null;
  }

  const stockItemId = getStockItemId(stockItem);
  const hasStockItemId =
    stockItemId !== undefined &&
    stockItemId !== null &&
    stockItemId !== "" &&
    Number.isInteger(Number(stockItemId));
  const itemAllocations = getSerialisedSubAssemblyAllocationsForStockItem(
    shipmentData,
    stockItem,
  );
  const missingCount = Math.max(
    Number(stockItem.count || 0) - itemAllocations.length,
    0,
  );

  const setAllocations = (SerialisedSubAssemblies) => {
    setShipmentData({
      ...shipmentData,
      SerialisedSubAssemblies,
    });
  };

  const removeAllocation = (subAssembly) => {
    setAllocations(allocations.filter((item) => item.id !== subAssembly.id));
  };

  const allocatedIds = allocations.map((item) => item.id);

  return (
    <Grid container spacing={1} alignItems="center">
      {itemAllocations.map((subAssembly) => (
        <AllocatedSubAssemblyLine
          key={subAssembly.id || subAssembly.number}
          subAssembly={subAssembly}
          editable={editable && !isClosedShipment}
          onRemove={removeAllocation}
        />
      ))}
      {hasStockItemId && !isClosedShipment ? (
        Array.from({ length: missingCount }).map((_, index) => {
          const allocationSlotKey = `${stockItemId}-${index}`;
          return (
            <Grid item xs={12} key={allocationSlotKey}>
              {editable && addingSerial === allocationSlotKey ? (
                <SerialisedSubAssemblySearchAutocomplete
                  fetchOnFocus={true}
                  excludeIds={allocatedIds}
                  searchRestrictions={{
                    StockItemId: stockItemId,
                    ...(shipmentData.pickupAddress
                      ? { lcLocation: shipmentData.pickupAddress }
                      : {}),
                  }}
                  onChange={(e, value) => {
                    const subAssemblyStockItemId =
                      getSubAssemblyStockItemId(value);
                    if (
                      !value ||
                      subAssemblyStockItemId?.toString() !==
                        stockItemId.toString() ||
                      allocations.find((item) => item.id === value.id)
                    ) {
                      return;
                    }
                    setAllocations([...allocations, value]);
                    setAddingSerial();
                  }}
                />
              ) : (
                <Tooltip
                  title={
                    <Typography style={{ fontSize: 15 }}>
                      {t(
                        "shipments.serialisedSubAssemblyRequired",
                        "Serialised sub-assembly needs to be assigned for this item",
                      )}
                    </Typography>
                  }
                >
                  <Button
                    startIcon={<Warning style={{ color: "yellow" }} />}
                    size="small"
                    variant="outlined"
                    disabled={!editable}
                    onClick={() => {
                      setAddingSerial(allocationSlotKey);
                    }}
                  >
                    {t(
                      "shipments.assignSerialisedSubAssembly",
                      "Assign sub-assembly",
                    )}
                  </Button>
                </Tooltip>
              )}
            </Grid>
          );
        })
      ) : missingCount && !isClosedShipment ? (
        <Grid item xs={12}>
          <Typography>
            {t(
              "shipments.stockItemReferenceRequired",
              "Stock item reference is required before allocating serialised sub-assemblies.",
            )}
          </Typography>
        </Grid>
      ) : null}
    </Grid>
  );
}

const AllocatedSubAssemblyLine = ({ subAssembly, editable, onRemove }) => (
  <Grid item xs={12} container alignItems="center">
    <Grid item>
      {subAssembly.id ? (
        <Link href={"/cpanel/manufacturing/serialisedsubassemblies"}>
          {subAssembly.number}
        </Link>
      ) : (
        <Typography>{subAssembly.number}</Typography>
      )}
    </Grid>
    <Grid item>
      <Typography style={{ marginLeft: 8 }}>
        {getSubAssemblyMeta(subAssembly)}
      </Typography>
    </Grid>
    {editable ? (
      <Grid item>
        <IconButton
          size="small"
          onClick={() => {
            onRemove(subAssembly);
          }}
        >
          <Delete />
        </IconButton>
      </Grid>
    ) : null}
  </Grid>
);
